import React, { useState, useEffect, useRef } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { toast } from "react-toastify";

import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────

const EMPTY_FORM = { title: "", description: "", image: "" };

// ─── CLOUDINARY UPLOAD FUNCTION ────────────────────────────────────────────
const uploadImage = async (file) => {
    // Using your client's Cloudinary credentials
    const CLOUD_NAME = "djgfcak5i";
    const UPLOAD_PRESET = "front_upload";
    
    const url = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", UPLOAD_PRESET);
    data.append("folder", "certifications");
    
    const res = await fetch(url, { method: "POST", body: data });
    const json = await res.json();
    return json.secure_url;
  };
// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

const ManageCertification = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [certifications, setCertifications] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletePopup, setDeletePopup] = useState({ visible: false, id: null });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const formTopRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "certifications"));
      const list = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by order field if exists, otherwise by createdAt
      list.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        if (a.createdAt && b.createdAt) {
          return b.createdAt.seconds - a.createdAt.seconds;
        }
        return a.title.localeCompare(b.title);
      });
      setCertifications(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch certifications.");
    } finally {
      setLoading(false);
    }
  };

  // ── DRAG AND DROP FOR SEQUENCE MANAGER ──
  const handleDragStart = (e, index) => {
    if (loading) {
      e.preventDefault();
      return false;
    }
    
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
    
    const dragImage = document.createElement('div');
    dragImage.textContent = certifications[index].title;
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    dragImage.style.background = '#FF8000';
    dragImage.style.color = 'white';
    dragImage.style.padding = '8px 16px';
    dragImage.style.borderRadius = '8px';
    dragImage.style.fontSize = '14px';
    dragImage.style.fontWeight = '600';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
    
    const item = e.target.closest('.sequence-item');
    if (item) {
      item.classList.add('dragging-source');
    }
  };

  const handleDragEnd = (e) => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    document.querySelectorAll('.sequence-item').forEach(item => {
      item.classList.remove('dragging-source', 'drag-over');
    });
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    const currentDragIndex = draggedIndex;
    
    setDragOverIndex(null);
    setDraggedIndex(null);
    
    document.querySelectorAll('.sequence-item').forEach(item => {
      item.classList.remove('dragging-source', 'drag-over');
    });
    
    if (currentDragIndex === null || currentDragIndex === dropIndex) {
      return;
    }

    const reorderedList = [...certifications];
    const [draggedItem] = reorderedList.splice(currentDragIndex, 1);
    reorderedList.splice(dropIndex, 0, draggedItem);

    const updatedList = reorderedList.map((item, idx) => ({
      ...item,
      order: idx
    }));

    setCertifications(updatedList);

    setLoading(true);
    try {
      const updatePromises = updatedList.map((item) =>
        updateDoc(doc(db, "certifications", item.id), { order: item.order })
      );
      await Promise.all(updatePromises);
      toast.success(" Sequence updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update sequence.");
      fetchCertifications();
    } finally {
      setLoading(false);
    }
  };

  const moveUp = async (index) => {
    if (index === 0) return;
    const reorderedList = [...certifications];
    [reorderedList[index - 1], reorderedList[index]] = [reorderedList[index], reorderedList[index - 1]];
    
    const updatedList = reorderedList.map((item, idx) => ({ ...item, order: idx }));
    setCertifications(updatedList);
    
    setLoading(true);
    try {
      const updatePromises = updatedList.map((item) =>
        updateDoc(doc(db, "certifications", item.id), { order: item.order })
      );
      await Promise.all(updatePromises);
      toast.success("✨ Sequence updated!");
    } catch (err) {
      console.error(err);
      fetchCertifications();
    } finally {
      setLoading(false);
    }
  };

  const moveDown = async (index) => {
    if (index === certifications.length - 1) return;
    const reorderedList = [...certifications];
    [reorderedList[index], reorderedList[index + 1]] = [reorderedList[index + 1], reorderedList[index]];
    
    const updatedList = reorderedList.map((item, idx) => ({ ...item, order: idx }));
    setCertifications(updatedList);
    
    setLoading(true);
    try {
      const updatePromises = updatedList.map((item) =>
        updateDoc(doc(db, "certifications", item.id), { order: item.order })
      );
      await Promise.all(updatePromises);
      toast.success("✨ Sequence updated!");
    } catch (err) {
      console.error(err);
      fetchCertifications();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    
    setSelectedFile(file);
  };

  // ── DRAG AND DROP FOR IMAGE UPLOAD ──
  const handleImageDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      if (!file.type.startsWith('image/')) {
        toast.error("Please drop an image file.");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
      
      setSelectedFile(file);
      
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
      }
      
      toast.success("Image dropped successfully!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      toast.error("Please fill Title and Description.");
      return;
    }

    setLoading(true);
    
    let imageUrl = formData.image;
    
    if (selectedFile) {
      setUploading(true);
      try {
        imageUrl = await uploadImage(selectedFile);
      } catch (err) {
        console.error(err);
        toast.error("Image upload failed.");
        setLoading(false);
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }
    
    try {
      const certificationData = {
        ...formData,
        image: imageUrl,
      };
      
      if (editingId) {
        await updateDoc(doc(db, "certifications", editingId), certificationData);
        toast.success("Certification updated!");
        setEditingId(null);
      } else {
        const maxOrder = certifications.length > 0 
          ? Math.max(...certifications.map(c => c.order || 0))
          : -1;
        
        await addDoc(collection(db, "certifications"), {
          ...certificationData,
          order: maxOrder + 1,
          createdAt: serverTimestamp(),
        });
        toast.success("Certification added!");
      }

      setFormData(EMPTY_FORM);
      setImagePreview("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchCertifications();
    } catch (err) {
      console.error(err);
      toast.error("Error saving certification.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (certification) => {
    setFormData({
      title: certification.title || "",
      description: certification.description || "",
      image: certification.image || "",
    });
    setImagePreview(certification.image || "");
    setSelectedFile(null);
    setEditingId(certification.id);
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDelete = (id) => setDeletePopup({ visible: true, id });

  const confirmDelete = async () => {
    if (!deletePopup.id) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, "certifications", deletePopup.id));
      toast.success("Certification deleted!");
      fetchCertifications();
    } catch (err) {
      console.error(err);
      toast.error("Error deleting certification.");
    } finally {
      setLoading(false);
      setDeletePopup({ visible: false, id: null });
    }
  };

  const cancelDelete = () => setDeletePopup({ visible: false, id: null });

  return (
    <div className="postjob-container" ref={formTopRef}>

      {/* ── Delete Popup ── */}
      {deletePopup.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "32px",
              borderRadius: "16px",
              textAlign: "center",
              width: "360px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <span style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}>!</span>
            </div>
            <h3 style={{ margin: "0 0 12px 0", color: "#2D3748", fontSize: "18px" }}>
              Confirm Delete
            </h3>
            <p style={{ margin: "0 0 28px 0", color: "#718096", fontSize: "14px", lineHeight: "1.4" }}>
              Are you sure you want to delete this certification?
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={{
                  backgroundColor: "transparent",
                  color: "#718096",
                  padding: "12px 24px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  cursor: "pointer",
                  flex: 1,
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => { e.target.style.backgroundColor = "#f7fafc"; e.target.style.color = "#4a5568"; }}
                onMouseOut={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#718096"; }}
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                style={{
                  background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
                  color: "white",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "600",
                  flex: 1,
                  boxShadow: "0 4px 12px rgba(245, 101, 101, 0.3)",
                  transition: "transform 0.2s",
                }}
                onMouseOver={(e) => (e.target.style.transform = "translateY(-1px)")}
                onMouseOut={(e) => (e.target.style.transform = "translateY(0)")}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Heading ── */}
      <h2 className="page-heading">
        <i className="fas fa-certificate" style={{ color: "#FF8000" }}></i>
        {editingId ? " Edit Certification" : " Add New Certification"}
      </h2>

      {/* ── Form ── */}
      <form onSubmit={handleSubmit} className="postjob-form">

        {/* 1 — Title */}
        <input
          type="text"
          name="title"
          placeholder="Certification Title (e.g., P@SHA, PSEB, GTCCI)"
          value={formData.title}
          onChange={handleChange}
          required
          style={{ marginBottom: "16px", width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "16px" }}
        />

        {/* 2 — Description */}
        <textarea
          name="description"
          placeholder="Certification Description (e.g., Pakistan Software Houses Association for IT & ITES)"
          value={formData.description}
          onChange={handleChange}
          required
          rows="4"
          style={{ marginBottom: "16px", width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", fontFamily: "inherit", resize: "vertical" }}
        />

        {/* 3 — Image Upload with Drag & Drop */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#4a5568" }}>
            Certification Image
          </label>
          
          {imagePreview && (
            <div style={{ marginBottom: "12px" }}>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{ 
                  width: "120px", 
                  height: "120px", 
                  objectFit: "contain",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "8px",
                  backgroundColor: "#f9fafb"
                }} 
              />
            </div>
          )}
          
          <label
            onDragEnter={handleImageDragEnter}
            onDragLeave={handleImageDragLeave}
            onDragOver={handleImageDragOver}
            onDrop={handleImageDrop}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "8px",
              width: "100%",
              minHeight: "100px",
              padding: "20px 16px",
              border: isDragOver ? "2px dashed #FF8000" : "1px solid #e2e8f0",
              borderRadius: "10px",
              fontSize: "14px",
              backgroundColor: isDragOver ? "#fff7ed" : "#f9fafb",
              cursor: "pointer",
              color: isDragOver ? "#FF8000" : "#6b7280",
              transition: "all 0.2s ease",
              boxSizing: "border-box",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
            onMouseEnter={e => {
              if (!isDragOver) {
                e.currentTarget.style.borderColor = "#FF8000";
                e.currentTarget.style.backgroundColor = "#fff7ed";
                e.currentTarget.style.color = "#FF8000";
              }
            }}
            onMouseLeave={e => {
              if (!isDragOver) {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.backgroundColor = "#f9fafb";
                e.currentTarget.style.color = "#6b7280";
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />

            {/* Upload Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>

            {/* Label Text */}
            <span style={{ fontWeight: "500", fontSize: "14px" }}>
              {isDragOver ? "Drop image here" : (fileInputRef.current?.files?.[0]?.name || "Click or drag & drop image here")}
            </span>
            <span style={{ fontSize: "12px", opacity: 0.7 }}>
              PNG, JPG, WEBP supported (Max 5MB)
            </span>
          </label>
        </div>
        <br />
        
        {/* Submit Buttons */}
        <div style={{ display: "flex", gap: "12px", marginTop: "8px", flexWrap: "wrap" }}>
          <button
            type="submit"
            disabled={loading || uploading}
            style={{
              backgroundColor: "#FF8000",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "8px",
              cursor: loading || uploading ? "not-allowed" : "pointer",
              fontSize: "16px",
              fontWeight: "600",
              opacity: loading || uploading ? 0.7 : 1,
              width: "auto",
              display: "inline-block",
            }}
          >
            {loading || uploading ? "Saving..." : editingId ? "Update Certification" : "Add Certification"}
          </button>

          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setFormData(EMPTY_FORM);
                setImagePreview("");
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              style={{
                backgroundColor: "transparent",
                color: "#718096",
                padding: "12px 24px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
                width: "auto",
                display: "inline-block",
              }}
              onMouseOver={(e) => { e.target.style.backgroundColor = "#f7fafc"; }}
              onMouseOut={(e) => { e.target.style.backgroundColor = "transparent"; }}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {/* ── SEQUENCE MANAGER (DRAG & DROP) ── */}
      <div style={{ marginTop: "32px", marginBottom: "32px" }}>
        <div style={{
          background: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          overflow: "hidden",
        }}>
          <div style={{
            background: "#FF8000",
            padding: "16px 24px",
            borderBottom: "1px solid rgba(255,255,255,0.2)",
          }}>
            <h3 style={{ margin: 0, color: "white", display: "flex", alignItems: "center", gap: "10px", fontSize: "18px" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="16 12 12 8 8 12" />
                <line x1="12" y1="16" x2="12" y2="8" />
              </svg>
              Sequence Manager (Drag & Drop to Reorder)
            </h3>
          </div>
          
          <div style={{ padding: "16px", maxHeight: "400px", overflowY: "auto" }}>
            <style>{`
              .sequence-item {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px;
                margin-bottom: 8px;
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                cursor: grab;
                transition: all 0.3s ease;
              }
              
              .sequence-item:active {
                cursor: grabbing;
              }
              
              .sequence-item.dragging-source {
                opacity: 0.4;
                background: #fff7ed;
                border-color: #FF8000;
              }
              
              .sequence-item.drag-over {
                border-top: 2px solid #FF8000;
                background: #fff7ed;
              }
              
              .sequence-item:hover {
                background: white;
                border-color: #FF8000;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              }
              
              .drag-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                background: white;
                border-radius: 6px;
                color: #64748b;
                transition: all 0.2s ease;
                cursor: grab;
                border: 1px solid #e2e8f0;
              }
              
              .sequence-item:hover .drag-icon {
                background: #FF8000;
                color: white;
                border-color: #FF8000;
              }
              
              .sequence-number {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                background: #FF8000;
                color: white;
                border-radius: 50%;
                font-weight: bold;
                font-size: 12px;
              }
              
              .sequence-title {
                flex: 1;
                font-weight: 500;
                color: black;
                font-size: 14px;
              }
              
              .seq-move-btn {
                width: 28px;
                height: 28px;
                border-radius: 6px;
                border: 1px solid #e2e8f0;
                background: white;
                cursor: pointer;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #FF8000;
                font-size: 12px;
              }
              
              .seq-move-btn:hover:not(:disabled) {
                background: #FF8000;
                color: white;
                transform: translateY(-1px);
              }
              
              .seq-move-btn:disabled {
                opacity: 0.3;
                cursor: not-allowed;
              }
            `}</style>
            
            {loading ? (
              <LoadingAnimation />
            ) : certifications.length === 0 ? (
              <p style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                No certifications added yet.
              </p>
            ) : (
              certifications.map((cert, index) => (
                <div
                  key={cert.id}
                  className={`sequence-item ${draggedIndex === index ? "dragging-source" : ""} ${dragOverIndex === index ? "drag-over" : ""}`}
                  draggable={!loading}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <div className="drag-icon" title="Drag to reorder">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="9" cy="12" r="1.5" />
                      <circle cx="15" cy="12" r="1.5" />
                      <circle cx="9" cy="16" r="1.5" />
                      <circle cx="15" cy="16" r="1.5" />
                      <circle cx="9" cy="8" r="1.5" />
                      <circle cx="15" cy="8" r="1.5" />
                    </svg>
                  </div>
                  
                  <div className="sequence-number">
                    {index + 1}
                  </div>
                  
                  <div className="sequence-title">
                    {cert.title}
                  </div>
                  
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0 || loading}
                      className="seq-move-btn"
                      title="Move Up"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === certifications.length - 1 || loading}
                      className="seq-move-btn"
                      title="Move Down"
                    >
                      ▼
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div style={{
          marginTop: "12px",
          padding: "12px",
          background: "#fef3c7",
          borderRadius: "8px",
          fontSize: "12px",
          color: "#92400e",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            <strong>Tip:</strong> Drag and drop items to reorder them. The sequence will automatically update on your website.
          </span>
        </div>
      </div>

      {/* ── Certifications Table ── */}
      <div className="job-records">
        <h3>All Certifications</h3>
        {loading ? (
          <LoadingAnimation />
        ) : certifications.length === 0 ? (
          <p>No certifications added yet.</p>
        ) : (
          <div className="table-wrapper">
            <table className="job-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f2f2f2", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px", textAlign: "left", width: "80px" }}>#</th>
                  <th style={{ padding: "12px", textAlign: "left", width: "80px" }}>Image</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Title</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Description</th>
                  <th style={{ padding: "12px", textAlign: "center", width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certifications.map((cert, index) => (
                  <tr key={cert.id} style={{ borderBottom: "1px solid #e2e8f0", verticalAlign: "middle" }}>
                    <td style={{ padding: "12px", textAlign: "center", verticalAlign: "middle" }}>
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                        background: "#FF8000",
                        color: "white",
                        borderRadius: "50%",
                        fontSize: "12px",
                        fontWeight: "bold"
                      }}>
                        {index + 1}
                      </span>
                    </td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      <div
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "2px solid #e2e8f0",
                          backgroundColor: "#f7fafc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px",
                        }}
                      >
                        {cert.image ? (
                          <img src={cert.image} alt={cert.title} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        ) : (
                          <i className="fas fa-certificate" style={{ color: "#cbd5e0", fontSize: "24px" }}></i>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "12px", fontWeight: "600", verticalAlign: "middle" }}>{cert.title}</td>
                    <td style={{ padding: "12px", verticalAlign: "middle" }}>
                      <span style={{ fontSize: "13px", color: "#666", lineHeight: "1.4" }}>
                        {cert.description.length > 100 ? cert.description.substring(0, 100) + "..." : cert.description}
                      </span>
                    </td>
                    <td className="action_buttons" style={{ padding: "12px", textAlign: "center", verticalAlign: "middle" }}>
                      <button onClick={() => handleEdit(cert)} style={{ marginRight: "8px" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
                          <path fill="currentColor" d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
                        </svg>
                      </button>
                      <button onClick={() => handleDelete(cert.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCertification;